// Offline storage using IndexedDB — works without HTTPS

const DB_NAME = 'audiowalk_offline';
const DB_VERSION = 1;
const STORE_NAME = 'files';
const META_STORE = 'meta';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE);
      }
    };
  });
}

// Save a file (blob) by URL key
export async function saveFile(url, blob) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(blob, url);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Get a file by URL key — returns blob or null
export async function getFile(url) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(url);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

// Check if a file is cached
export async function isFileCached(url) {
  const blob = await getFile(url);
  return !!blob;
}

// Save excursion metadata (JSON)
export async function saveMeta(excursionId, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readwrite');
    tx.objectStore(META_STORE).put(data, `exc_${excursionId}`);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Get excursion metadata
export async function getMeta(excursionId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readonly');
    const req = tx.objectStore(META_STORE).get(`exc_${excursionId}`);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

// Download a URL and save to IndexedDB
async function downloadAndSave(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${url}`);
  const blob = await response.blob();
  await saveFile(url, blob);
  return blob;
}

// Download all media for an excursion
// onProgress(downloaded, total) callback
export async function downloadExcursion(excursionId, mediaUrls, stepsData, onProgress) {
  const total = mediaUrls.length;
  let downloaded = 0;

  // Save steps data for offline use
  await saveMeta(excursionId, stepsData);

  for (const url of mediaUrls) {
    try {
      // Skip if already cached
      const existing = await getFile(url);
      if (!existing) {
        await downloadAndSave(url);
      }
    } catch (e) {
      console.warn('Failed to download:', url, e);
    }
    downloaded++;
    if (onProgress) onProgress(downloaded, total);
  }
}

// Check if excursion is fully downloaded
export async function isExcursionDownloaded(excursionId, mediaUrls) {
  const meta = await getMeta(excursionId);
  if (!meta) return false;

  for (const url of mediaUrls) {
    const cached = await isFileCached(url);
    if (!cached) return false;
  }
  return true;
}

// Get a URL that works offline — returns blob URL if cached, otherwise original URL
export async function getOfflineUrl(url) {
  try {
    const blob = await getFile(url);
    if (blob) return URL.createObjectURL(blob);
  } catch (e) { /* ignore */ }
  return url;
}

// Delete all offline files for an excursion
export async function deleteExcursion(excursionId, mediaUrls) {
  const db = await openDB();
  // Delete all media files
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const url of mediaUrls) {
      store.delete(url);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  // Delete metadata
  await new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readwrite');
    tx.objectStore(META_STORE).delete(`exc_${excursionId}`);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
